import { FocusKeyManager } from '@angular/cdk/a11y';
import { ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit,
  Output, QueryList, ViewChild, ViewChildren
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { EMPTY } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { SearchEntitiesPublicGQL } from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { isNilOrEmpty } from 'helpers';
import { ListItemComponent } from 'shared/components/list-item/list-item.component';

@Component({
  selector: 'app-entity-quick-search',
  templateUrl: './entity-quick-search.component.html',
})
export class EntityQuickSearchComponent extends WithDisposableSubject() implements OnInit, OnDestroy {
  @ViewChild('resultsEl', { static: true }) resultsEl: ElementRef;

  @ViewChildren(ListItemComponent) items: QueryList<ListItemComponent>;

  @Output() entitySelected = new EventEmitter<{ id: string }>();

  @Input() campaignId: string;

  keyManager: FocusKeyManager<ListItemComponent>;
  // search inputs
  searchInput = new UntypedFormControl();
  searchResults = [];
  noResults = false;
  loadingResults = false;

  // Handles clicks outside of the search results
  // and close it if it is open
  @HostListener('document:click', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  handleOutsideClick(event) {
    if (this.searchResults.length !== 0 && !this.resultsEl.nativeElement.contains(event.target)) {
      this.searchInput.setValue(null);
    }
  }

  constructor(
    private _cd: ChangeDetectorRef,
    private _searchEntitysPublicGQL: SearchEntitiesPublicGQL
  ) {
    super();
  }

  ngOnInit(): void {
    this.searchInput.valueChanges.pipe(
      takeUntil(this.disposer),
      tap((value) => {
        // If empty value is passed, reset the the searchResults to be empty, and loading to be false, and noResults to be false
        if (isNilOrEmpty(value)) {
          this.searchResults = [];
          this.noResults = false;
          this.loadingResults = false;
        }
      }),
      // Only start to search when length is greater than 3
      filter(value => !isNilOrEmpty(value) && value.length >= 3),
      tap(() => this.loadingResults = true),
      debounceTime(400),
      switchMap((value) => this._searchEntitysPublicGQL.fetch({
        keyword: value,
        campaignId: this.campaignId,
        assessmentCampaignId: this.campaignId,
        accessibleEntitiesOnly: true,
        turfOnly: true
      },
      {
        fetchPolicy: 'network-only'
      }
      ).pipe(catchError(() => EMPTY)))
    )
      .subscribe(
        (result) => {
          const searchResults = result.data.searchEntitiesPublic.nodes;
          this.searchResults = searchResults;
          this.noResults = searchResults.length === 0;
          this.loadingResults = false;
          this.keyManager = new FocusKeyManager(this.items).withWrap();
          this._cd.markForCheck();
        },
        (_err) => {
          this.loadingResults = false;
          this._cd.markForCheck();
        });
  }

  setFirstItemActive() {
    this.keyManager.setFirstItemActive();
  }

  onKeydown(event) {
    if (event.keyCode === ENTER) {
      this.onEntitySelected(this.keyManager.activeItem.item);
    } else {
      this.keyManager.onKeydown(event);
    }
  }

  onEntitySelected(entity: { id: string }) {
    // Set value to null to clear input and close the results.
    this.searchInput.setValue(null);
    this.entitySelected.emit(entity);
  }

}
